package br.com.htms.inspeccaocampo.ui.screens

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.material3.Button
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.ExposedDropdownMenuBox
import androidx.compose.material3.ExposedDropdownMenuDefaults
import androidx.compose.material3.FloatingActionButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.RadioButton
import androidx.compose.material3.Scaffold
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextDecoration
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import br.com.htms.inspeccaocampo.data.AcaoCorrecaoChecklist
import br.com.htms.inspeccaocampo.data.ChecklistAlojamentoCatalog
import br.com.htms.inspeccaocampo.data.RespostaConformidade
import br.com.htms.inspeccaocampo.data.RespostaSimNao
import br.com.htms.inspeccaocampo.form.AppController
import br.com.htms.inspeccaocampo.shared.ui.components.ButtonLoadingContent
import br.com.htms.inspeccaocampo.shared.ui.components.FormFieldDefaults
import br.com.htms.inspeccaocampo.shared.ui.components.ItemConformidadeCard
import br.com.htms.inspeccaocampo.shared.ui.components.LegendaConformidade
import br.com.htms.inspeccaocampo.shared.ui.components.SignatureField
import br.com.htms.inspeccaocampo.shared.ui.components.SignaturePadScreen
import kotlinx.coroutines.launch

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun SharedChecklistAlojamentoScreen(
    controller: AppController,
    modoCombinado: Boolean = false
) {
    val form by controller.checklistForm.collectAsState()
    val erros by controller.erros.collectAsState()
    val mensagem by controller.mensagem.collectAsState()
    val obrasAtivas by controller.obrasAtivas.collectAsState()
    val assinaturaEditando by controller.assinaturaEditando.collectAsState()
    val capturandoGps by controller.capturandoGps.collectAsState()

    val snackbar = remember { SnackbarHostState() }
    val scope = rememberCoroutineScope()
    val listState = rememberLazyListState()
    var menuObraAberto by remember { mutableStateOf(false) }

    fun scrollSeguro(index: Int) {
        scope.launch {
            val total = listState.layoutInfo.totalItemsCount
            if (total <= 0) return@launch
            listState.animateScrollToItem(index.coerceIn(0, total - 1))
        }
    }

    LaunchedEffect(Unit) {
        controller.carregarObrasAtivas()
    }

    LaunchedEffect(mensagem) {
        val msg = mensagem ?: return@LaunchedEffect
        snackbar.showSnackbar(msg)
        controller.consumirMensagem()
    }

    LaunchedEffect(erros) {
        if (erros.isEmpty()) return@LaunchedEffect
        runCatching { listState.animateScrollToItem(0) }
    }

    if (assinaturaEditando != null) {
        val campo = assinaturaEditando!!
        val titulo = when (campo) {
            "inspecionadoPor" -> "Assinatura — Inspecionado por"
            "acompanhadoPor" -> "Assinatura — Acompanhado por"
            "engenheiroResidente" -> "Assinatura — Engenheiro residente"
            else -> "Assinatura"
        }
        SignaturePadScreen(
            titulo = titulo,
            onCancelar = { controller.fecharAssinatura() },
            onConfirmar = { token -> controller.salvarAssinaturaChecklist(campo, token) }
        )
        return
    }

    val secoes = ChecklistAlojamentoCatalog.secoesPadrao()
    val temNc = form.itens.any { it.resposta == RespostaConformidade.NC }
    val coordsTexto = when {
        form.latitude != null && form.longitude != null ->
            "Lat: ${form.latitude}  Lng: ${form.longitude}"
        else -> ""
    }

    Scaffold(
        snackbarHost = { SnackbarHost(snackbar) }
    ) { padding ->
        Box(
            modifier = Modifier
                .fillMaxSize()
                .padding(padding)
                .background(Color.Transparent)
        ) {
            LazyColumn(
                state = listState,
                modifier = Modifier.fillMaxSize(),
                contentPadding = PaddingValues(bottom = 96.dp),
                verticalArrangement = Arrangement.spacedBy(12.dp)
            ) {
                item {
                    OutlinedButton(
                        onClick = { controller.voltarAoHub() },
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(horizontal = 16.dp, vertical = 8.dp)
                    ) { Text("Voltar") }
                }

                item {
                    LegendaConformidade(
                        modifier = Modifier.padding(horizontal = 16.dp)
                    )
                }

                item {
                    Card(
                        modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp),
                        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface)
                    ) {
                        Column(Modifier.padding(12.dp), verticalArrangement = Arrangement.spacedBy(10.dp)) {
                            Text("Dados da inspeção", fontWeight = FontWeight.Bold)
                            OutlinedTextField(
                                value = form.endereco,
                                onValueChange = { v ->
                                    controller.atualizarChecklist { it.copy(endereco = v) }
                                },
                                label = { Text("Endereço *") },
                                modifier = Modifier.fillMaxWidth(),
                                minLines = 2,
                                colors = FormFieldDefaults.textFieldColors(),
                                isError = controller.erroNoCampo("endereco") != null
                            )
                            ExposedDropdownMenuBox(
                                expanded = menuObraAberto,
                                onExpandedChange = { menuObraAberto = it }
                            ) {
                                OutlinedTextField(
                                    value = form.obra.ifBlank { "Selecione a obra" },
                                    onValueChange = {},
                                    readOnly = true,
                                    label = { Text("Obra *") },
                                    trailingIcon = {
                                        ExposedDropdownMenuDefaults.TrailingIcon(expanded = menuObraAberto)
                                    },
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .menuAnchor(),
                                    colors = FormFieldDefaults.textFieldColors(),
                                    isError = controller.erroNoCampo("obra") != null,
                                    singleLine = true
                                )
                                DropdownMenu(
                                    expanded = menuObraAberto,
                                    onDismissRequest = { menuObraAberto = false }
                                ) {
                                    obrasAtivas.forEach { obraItem ->
                                        DropdownMenuItem(
                                            text = {
                                                Text(
                                                    obraItem.nome,
                                                    maxLines = 2,
                                                    overflow = TextOverflow.Ellipsis
                                                )
                                            },
                                            onClick = {
                                                controller.atualizarChecklist { it.copy(obra = obraItem.nome) }
                                                menuObraAberto = false
                                            }
                                        )
                                    }
                                }
                            }
                            OutlinedTextField(
                                value = form.dataHorarioFormatado(),
                                onValueChange = {},
                                readOnly = true,
                                label = { Text("Data e horário (automático)") },
                                modifier = Modifier.fillMaxWidth(),
                                colors = FormFieldDefaults.textFieldColors()
                            )
                        }
                    }
                }

                item {
                    Card(
                        modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp),
                        colors = CardDefaults.cardColors(containerColor = Color(0xFFB3C9E8))
                    ) {
                        Column(Modifier.padding(12.dp), verticalArrangement = Arrangement.spacedBy(10.dp)) {
                            Text("Local (GPS)", fontWeight = FontWeight.Bold)
                            OutlinedTextField(
                                value = form.localTexto.ifBlank { "Aguardando GPS..." },
                                onValueChange = {},
                                readOnly = true,
                                label = { Text("Local (GPS)") },
                                modifier = Modifier.fillMaxWidth(),
                                colors = FormFieldDefaults.textFieldColors(),
                                isError = controller.erroNoCampo("localGps") != null
                            )
                            if (coordsTexto.isNotBlank()) {
                                Text(
                                    coordsTexto,
                                    style = MaterialTheme.typography.bodySmall,
                                    color = MaterialTheme.colorScheme.onSurfaceVariant
                                )
                            }
                            OutlinedButton(
                                onClick = { controller.capturarGpsChecklist() },
                                enabled = !capturandoGps,
                                modifier = Modifier.fillMaxWidth()
                            ) {
                                ButtonLoadingContent(
                                    loading = capturandoGps,
                                    loadingText = "Capturando GPS...",
                                    idleText = "Atualizar local por GPS"
                                )
                            }
                        }
                    }
                }

                secoes.forEach { secao ->
                    val itensSecao = form.itens.filter { it.secao == secao.titulo }
                    if (itensSecao.isEmpty()) return@forEach
                    item {
                        Text(
                            secao.titulo,
                            modifier = Modifier.padding(horizontal = 16.dp),
                            fontWeight = FontWeight.Bold
                        )
                    }
                    items(itensSecao, key = { it.id }) { item ->
                        ItemConformidadeCard(
                            item = item,
                            erro = controller.erroNoCampo(item.id),
                            onResposta = { controller.definirRespostaChecklist(item.id, it) },
                            onJustificativa = { txt ->
                                controller.atualizarItemChecklist(item.id) { it.copy(justificativa = txt) }
                            },
                            modifier = Modifier.padding(horizontal = 16.dp)
                        )
                    }
                }

                item {
                    Card(
                        modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp),
                        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface)
                    ) {
                        Column(Modifier.padding(12.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                            Text("Liberado para uso *", fontWeight = FontWeight.Bold)
                            RespostaSimNao.entries.forEach { opcao ->
                                Row(
                                    verticalAlignment = Alignment.CenterVertically,
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .clickable(role = Role.RadioButton) {
                                            controller.atualizarChecklist { it.copy(liberadoParaUso = opcao) }
                                        }
                                        .padding(vertical = 4.dp)
                                ) {
                                    RadioButton(
                                        selected = form.liberadoParaUso == opcao,
                                        onClick = null
                                    )
                                    Text(opcao.rotulo)
                                }
                            }
                            controller.erroNoCampo("liberadoParaUso")?.let {
                                Text(it, color = MaterialTheme.colorScheme.error, style = MaterialTheme.typography.bodySmall)
                            }
                        }
                    }
                }

                if (temNc) {
                    item {
                        Card(
                            modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp),
                            colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.secondaryContainer)
                        ) {
                            Column(Modifier.padding(12.dp), verticalArrangement = Arrangement.spacedBy(10.dp)) {
                                Text("Ações de correção", fontWeight = FontWeight.Bold)
                                Text(
                                    "Itens não conformes identificados — registre ações corretivas.",
                                    style = MaterialTheme.typography.bodySmall
                                )
                                form.acoesCorrecao.forEachIndexed { index, acao ->
                                    AcaoCorrecaoRow(
                                        acao = acao,
                                        onChange = { atualizado ->
                                            controller.atualizarChecklist { f ->
                                                f.copy(
                                                    acoesCorrecao = f.acoesCorrecao.toMutableList().also {
                                                        it[index] = atualizado
                                                    }
                                                )
                                            }
                                        },
                                        onRemover = {
                                            controller.atualizarChecklist { f ->
                                                f.copy(acoesCorrecao = f.acoesCorrecao.filterIndexed { i, _ -> i != index })
                                            }
                                        }
                                    )
                                }
                                OutlinedButton(
                                    onClick = {
                                        controller.atualizarChecklist { f ->
                                            f.copy(acoesCorrecao = f.acoesCorrecao + AcaoCorrecaoChecklist())
                                        }
                                    },
                                    modifier = Modifier.fillMaxWidth()
                                ) { Text("Adicionar ação de correção") }
                            }
                        }
                    }
                }

                item {
                    Column(
                        Modifier.fillMaxWidth().padding(horizontal = 16.dp),
                        verticalArrangement = Arrangement.spacedBy(12.dp)
                    ) {
                        Text("Assinaturas", fontWeight = FontWeight.Bold)
                        AssinaturaChecklistBlock(
                            titulo = "Inspecionado por *",
                            nome = form.inspecionadoPor.nome,
                            assinatura = form.inspecionadoPor.assinatura,
                            erroNome = controller.erroNoCampo("inspecionadoPorNome"),
                            erroAss = controller.erroNoCampo("inspecionadoPorAss"),
                            onNomeChange = { v ->
                                controller.atualizarChecklist {
                                    it.copy(inspecionadoPor = it.inspecionadoPor.copy(nome = v))
                                }
                            },
                            onAssinar = { controller.abrirAssinatura("inspecionadoPor") }
                        )
                        AssinaturaChecklistBlock(
                            titulo = "Acompanhado por",
                            nome = form.acompanhadoPor.nome,
                            assinatura = form.acompanhadoPor.assinatura,
                            erroNome = null,
                            erroAss = null,
                            onNomeChange = { v ->
                                controller.atualizarChecklist {
                                    it.copy(acompanhadoPor = it.acompanhadoPor.copy(nome = v))
                                }
                            },
                            onAssinar = { controller.abrirAssinatura("acompanhadoPor") }
                        )
                        AssinaturaChecklistBlock(
                            titulo = "Engenheiro residente",
                            nome = form.engenheiroResidente.nome,
                            assinatura = form.engenheiroResidente.assinatura,
                            erroNome = null,
                            erroAss = null,
                            onNomeChange = { v ->
                                controller.atualizarChecklist {
                                    it.copy(engenheiroResidente = it.engenheiroResidente.copy(nome = v))
                                }
                            },
                            onAssinar = { controller.abrirAssinatura("engenheiroResidente") }
                        )
                    }
                }

                if (erros.isNotEmpty()) {
                    item {
                        Card(
                            modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp),
                            colors = CardDefaults.cardColors(
                                containerColor = MaterialTheme.colorScheme.errorContainer
                            )
                        ) {
                            Column(
                                modifier = Modifier.padding(12.dp),
                                verticalArrangement = Arrangement.spacedBy(6.dp)
                            ) {
                                Text("Corrija os campos obrigatórios:", fontWeight = FontWeight.Bold)
                                erros.take(5).forEach { erro ->
                                    Text(
                                        text = "• ${erro.mensagem}",
                                        color = MaterialTheme.colorScheme.onErrorContainer,
                                        textDecoration = TextDecoration.Underline,
                                        modifier = Modifier
                                            .fillMaxWidth()
                                            .clickable { scope.launch { listState.animateScrollToItem(0) } }
                                            .padding(vertical = 4.dp)
                                    )
                                }
                                if (erros.size > 5) {
                                    Text("... e mais ${erros.size - 5}")
                                }
                            }
                        }
                    }
                }

                item {
                    Button(
                        onClick = {
                            if (modoCombinado) {
                                controller.validarESalvarRelatorioAlojamento()
                            } else {
                                controller.validarESalvarChecklist()
                            }
                        },
                        modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp)
                    ) {
                        Text(if (modoCombinado) "Salvar (Parte 1 + Parte 2)" else "Salvar")
                    }
                }
            }

            Column(
                modifier = Modifier
                    .align(Alignment.BottomEnd)
                    .padding(16.dp),
                verticalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                FloatingActionButton(
                    onClick = { scrollSeguro(0) },
                    containerColor = MaterialTheme.colorScheme.primaryContainer
                ) {
                    Text("Início", fontWeight = FontWeight.Bold, style = MaterialTheme.typography.labelSmall)
                }
                FloatingActionButton(
                    onClick = {
                        val last = (listState.layoutInfo.totalItemsCount - 1).coerceAtLeast(0)
                        scrollSeguro(last)
                    },
                    containerColor = MaterialTheme.colorScheme.primaryContainer
                ) {
                    Text("Fim", fontWeight = FontWeight.Bold, style = MaterialTheme.typography.labelSmall)
                }
            }
        }
    }
}

@Composable
private fun AssinaturaChecklistBlock(
    titulo: String,
    nome: String,
    assinatura: String,
    erroNome: String?,
    erroAss: String?,
    onNomeChange: (String) -> Unit,
    onAssinar: () -> Unit
) {
    Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
        Text(titulo, fontWeight = FontWeight.SemiBold)
        OutlinedTextField(
            value = nome,
            onValueChange = onNomeChange,
            label = { Text("Nome") },
            modifier = Modifier.fillMaxWidth(),
            colors = FormFieldDefaults.textFieldColors(),
            isError = erroNome != null
        )
        SignatureField(
            label = titulo,
            assinaturaToken = assinatura,
            erro = erroAss,
            onClick = onAssinar,
            modifier = Modifier.fillMaxWidth()
        )
    }
}

@Composable
private fun AcaoCorrecaoRow(
    acao: AcaoCorrecaoChecklist,
    onChange: (AcaoCorrecaoChecklist) -> Unit,
    onRemover: () -> Unit
) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .padding(vertical = 4.dp),
        verticalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        OutlinedTextField(
            value = acao.item,
            onValueChange = { onChange(acao.copy(item = it)) },
            label = { Text("Item") },
            modifier = Modifier.fillMaxWidth(),
            colors = FormFieldDefaults.textFieldColors()
        )
        OutlinedTextField(
            value = acao.acao,
            onValueChange = { onChange(acao.copy(acao = it)) },
            label = { Text("Ação") },
            modifier = Modifier.fillMaxWidth(),
            minLines = 2,
            colors = FormFieldDefaults.textFieldColors()
        )
        OutlinedTextField(
            value = acao.responsavel,
            onValueChange = { onChange(acao.copy(responsavel = it)) },
            label = { Text("Responsável") },
            modifier = Modifier.fillMaxWidth(),
            colors = FormFieldDefaults.textFieldColors()
        )
        OutlinedTextField(
            value = acao.prazo,
            onValueChange = { onChange(acao.copy(prazo = it)) },
            label = { Text("Prazo") },
            modifier = Modifier.fillMaxWidth(),
            colors = FormFieldDefaults.textFieldColors()
        )
        OutlinedButton(onClick = onRemover, modifier = Modifier.fillMaxWidth()) {
            Text("Remover ação")
        }
    }
}
